import { useEffect, useState, useRef } from 'react';

interface GlobalPullToRefreshOptions {
  /**
   * Distance in pixels the user must pull down to trigger refresh
   * If not set, defaults to 1/3 of screen height
   * @default undefined (uses screen height calculation)
   */
  threshold?: number;
  /**
   * Maximum pull distance in pixels
   * @default 140
   */
  maxPullDistance?: number;
  /**
   * Maximum Y position (from top of screen) where a touch can start to trigger pull-to-refresh
   * Prevents accidental triggers from touches that start lower on the screen
   * @default 100
   */
  maxStartY?: number;
  /**
   * Callback function when refresh is triggered
   */
  onRefresh?: () => void | Promise<void>;
}

interface GlobalPullToRefreshState {
  isPulling: boolean;
  pullDistance: number;
  isRefreshing: boolean;
}

/**
 * Global document-level pull-to-refresh hook for hard reloading the app
 * Works anywhere in the app when at the top of the page
 * Designed to fix blank page issues after PWA deployments
 */
export function useGlobalPullToRefresh(options: GlobalPullToRefreshOptions = {}) {
  const {
    threshold,
    maxPullDistance,
    maxStartY = 100,
    onRefresh,
  } = options;

  // Calculate threshold based on screen height if not provided (default: 1/3 of screen)
  const getEffectiveThreshold = () => {
    if (threshold !== undefined) return threshold;
    return Math.floor(window.innerHeight / 3);
  };

  // Calculate max pull distance - should be slightly larger than threshold to give visual feedback
  const getEffectiveMaxPullDistance = () => {
    if (maxPullDistance !== undefined) return maxPullDistance;
    return Math.floor(window.innerHeight / 3) + 40; // threshold + 40px buffer
  };

  const [state, setState] = useState<GlobalPullToRefreshState>({
    isPulling: false,
    pullDistance: 0,
    isRefreshing: false,
  });

  const touchStartY = useRef<number>(0);
  const touchCurrentY = useRef<number>(0);

  // Use refs to track state values for use in event handlers
  // This prevents the effect from re-running on every state change
  const stateRef = useRef(state);
  const onRefreshRef = useRef(onRefresh);

  // Keep refs in sync with state/props
  stateRef.current = state;
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    let supportsPassive = false;
    try {
      const opts = Object.defineProperty({}, 'passive', {
        get() {
          supportsPassive = true;
        },
      });
      window.addEventListener('testPassive', null as any, opts);
      window.removeEventListener('testPassive', null as any, opts);
    } catch (e) {
      // Passive not supported
    }

    const handleTouchStart = (e: TouchEvent) => {
      // Only trigger if user is at the top of the page AND touch starts near the top of the screen
      const touchY = e.touches[0].clientY;
      if (window.scrollY === 0 && touchY <= maxStartY) {
        touchStartY.current = touchY;
      } else {
        // Reset to prevent partial pulls from triggering
        touchStartY.current = 0;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Use ref for isRefreshing to avoid stale closure
      if (stateRef.current.isRefreshing) return;
      // Skip if touch didn't start in the valid zone
      if (touchStartY.current === 0) return;

      touchCurrentY.current = e.touches[0].clientY;
      const pullDistance = Math.max(0, touchCurrentY.current - touchStartY.current);

      // Only trigger pull if at top of page and pulling down
      if (window.scrollY === 0 && pullDistance > 0) {
        // Prevent default scrolling behavior
        if (pullDistance > 10) {
          e.preventDefault();
        }

        const effectiveMaxPull = getEffectiveMaxPullDistance();
        const clampedDistance = Math.min(pullDistance, effectiveMaxPull);

        setState({
          isPulling: clampedDistance > 10,
          pullDistance: clampedDistance,
          isRefreshing: false,
        });
      }
    };

    const handleTouchEnd = async () => {
      const effectiveThreshold = getEffectiveThreshold();
      // Use ref to get current state values and avoid stale closure issues
      const currentState = stateRef.current;

      if (currentState.pullDistance >= effectiveThreshold && !currentState.isRefreshing) {
        setState({
          isPulling: false,
          pullDistance: 0,
          isRefreshing: true,
        });

        // Execute custom refresh callback if provided
        if (onRefreshRef.current) {
          await onRefreshRef.current();
        }

        // Hard reload to clear all caches
        window.location.reload();
      } else {
        setState({
          isPulling: false,
          pullDistance: 0,
          isRefreshing: false,
        });
      }
    };

    const passiveOption = supportsPassive ? { passive: false } : false;

    document.addEventListener('touchstart', handleTouchStart, passiveOption);
    document.addEventListener('touchmove', handleTouchMove, passiveOption);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  // Only include stable values in dependencies - refs handle the changing state
  }, [threshold, maxPullDistance, maxStartY]);

  return state;
}
